import { AutoCompleteSelectBox } from 'src/components/AutoCompleteSelectBox';
import { SkillCard } from './SkillCard';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useAction,
  useConnections,
  useIntegrationApp,
  useIntegrations,
} from '@membranehq/react';
import { Loading } from 'src/components/Loading';
import { Input } from 'src/components/Input';
import { Textarea } from 'src/components/Textarea';
import { CheckCircle, XCircle } from '@phosphor-icons/react';
import { Button } from 'src/components/Button';
import { Tab } from 'src/components/Tab';

type EmailTarget = {
  trigger_condition: string;
  from_email: string;
  subject: string;
  content_body: string;
};

type InCallEmailConfig = {
  integration_id: string;
  connection_id: string;
  email_targets: EmailTarget[];
};

const EMAIL_BODY_PLACEHOLDER = `Hi,

This is the information you requested: ...

Cheers,
[Your Name]`;

export const validateInCallEmailConfig = (
  config: InCallEmailConfig | undefined,
): boolean => {
  if (
    !config ||
    !config.integration_id ||
    !config.email_targets ||
    config.email_targets.length === 0
  ) {
    return false;
  }
  return config.email_targets.every((target) => {
    return (
      !!target.trigger_condition.trim() &&
      !!target.from_email.trim() &&
      !!target.subject.trim() &&
      !!target.content_body.trim()
    );
  });
};

const SEND_EMAIL_ACTION_KEY = 'send_email';

export function InCallEmailSkill({
  config,
  onConfigChange,
}: {
  config: InCallEmailConfig;
  onConfigChange?: (config: InCallEmailConfig) => void;
}) {
  const integrationApp = useIntegrationApp();
  const { refresh: refreshIntegrations } = useIntegrations();
  const { action, loading: actionLoading } = useAction(SEND_EMAIL_ACTION_KEY);
  const { items: connections } = useConnections();

  const [selectedEmailIntegration, setSelectedEmailIntegration] =
    useState<string>(config.integration_id || '');
  const [activeTab, setActiveTab] = useState<number>(0);

  const integrations = useMemo(
    () =>
      (action?.appliedToIntegrations ?? []).map(
        (appliedToIntegration) => appliedToIntegration.integration,
      ),
    [action],
  );

  useEffect(() => {
    if (!config.email_targets || config.email_targets.length === 0) {
      onConfigChange?.({
        ...config,
        email_targets: [
          {
            trigger_condition: '',
            from_email: '',
            subject: '',
            content_body: '',
          },
        ],
      });
    }
  }, []);

  const connectedEmailIntegrations = useMemo(
    () =>
      connections
        .filter(
          (connection) =>
            connection.integration &&
            integrations.some(
              (integration) => integration.key === connection.integration!.key,
            ),
        )
        .map((connection) => connection.integration),
    [connections, integrations],
  );

  const searchEmailIntegrations = useCallback(
    async (query: string) => {
      return integrations
        .filter((integration) =>
          integration.name.toLowerCase().includes(query.toLowerCase()),
        )
        .map((integration) => ({
          value: integration.key,
          label: integration.name,
        }));
    },
    [integrations],
  );

  return (
    <SkillCard description="Configure the email integration to send emails from during a call">
      {actionLoading ? (
        <div className="flex justify-center items-center h-32">
          <Loading />
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-2">
            <div className="flex-1">
              <AutoCompleteSelectBox
                placeholder="Email Provider"
                defaultValue={
                  selectedEmailIntegration
                    ? {
                        value: selectedEmailIntegration,
                        label: integrations.find(
                          (integration) =>
                            integration.key === selectedEmailIntegration,
                        )?.name,
                      }
                    : undefined
                }
                onChange={(value) => {
                  // Prompt user to connect if not already connected
                  if (
                    !connectedEmailIntegrations.find(
                      (integration) => integration!.key === value,
                    )
                  ) {
                    integrationApp.integration(value).open({
                      onClose: async () => {
                        const integration = await integrationApp
                          .integration(value)
                          .get();
                        if (integration.connection) {
                          await refreshIntegrations();
                          setSelectedEmailIntegration(value);
                          onConfigChange?.({
                            ...config,
                            integration_id: value,
                            connection_id: integration.connection.id,
                          });
                          return;
                        }
                        setSelectedEmailIntegration('');
                        onConfigChange?.({
                          ...config,
                          integration_id: '',
                          connection_id: '',
                        });
                      },
                    });
                  } else {
                    setSelectedEmailIntegration(value);
                    onConfigChange?.({
                      ...config,
                      integration_id: value,
                      connection_id:
                        connectedEmailIntegrations.find(
                          (integration) => integration?.key === value,
                        )?.id || '',
                    });
                  }
                }}
                fetchOptions={searchEmailIntegrations}
              />
            </div>
            {connectedEmailIntegrations.some(
              (integration) => integration!.key === selectedEmailIntegration,
            ) ? (
              <div className="flex items-center gap-2">
                <CheckCircle size={24} className="text-success" />
                <span>Connected</span>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <XCircle size={24} className="text-error" />
                <span>Not Connected</span>
              </div>
            )}
          </div>
          <Tab
            selectedIndex={activeTab}
            onChange={setActiveTab}
            items={[
              ...config.email_targets.map((target, index) => ({
                label: target.subject || 'Email',
                content: (
                  <InCallEmailTarget
                    key={index}
                    index={index}
                    config={config}
                    onConfigChange={onConfigChange}
                  />
                ),
              })),
              {
                label: (
                  <div
                    className="font-semibold"
                    onClick={() => {
                      onConfigChange?.({
                        ...config,
                        email_targets: [
                          ...config.email_targets,
                          {
                            trigger_condition: '',
                            from_email: '',
                            subject: '',
                            content_body: '',
                          },
                        ],
                      });
                    }}
                  >
                    + Add email
                  </div>
                ),
                content: null,
              },
            ]}
          />
        </div>
      )}
    </SkillCard>
  );
}

const InCallEmailTarget = ({
  index,
  config,
  onConfigChange,
}: {
  index: number;
  config: InCallEmailConfig;
  onConfigChange?: (config: InCallEmailConfig) => void;
}) => {
  const target: EmailTarget = useMemo(
    () => config.email_targets[index],
    [config.email_targets, index],
  );
  return (
    <div className="flex flex-col gap-4 border p-4 rounded-md">
      <Input
        label="When should this email be sent?"
        placeholder="e.g., When user asks for product details"
        value={target.trigger_condition}
        onChange={(e) => {
          onConfigChange?.({
            ...config,
            email_targets: config.email_targets.map((t, i) =>
              i === index ? { ...t, trigger_condition: e.target.value } : t,
            ),
          });
        }}
        required
      />
      <Input
        label="From Email"
        placeholder="from@your.company.com"
        value={target.from_email}
        onChange={(e) => {
          onConfigChange?.({
            ...config,
            email_targets: config.email_targets.map((t, i) =>
              i === index ? { ...t, from_email: e.target.value } : t,
            ),
          });
        }}
        required
      />
      <Input
        label="Subject"
        placeholder="Details of what we discussed during the call..."
        value={target.subject}
        onChange={(e) => {
          onConfigChange?.({
            ...config,
            email_targets: config.email_targets.map((t, i) =>
              i === index ? { ...t, subject: e.target.value } : t,
            ),
          });
        }}
        required
      />
      <Textarea
        label="Body"
        placeholder={EMAIL_BODY_PLACEHOLDER}
        value={target.content_body}
        onChange={(e) => {
          onConfigChange?.({
            ...config,
            email_targets: config.email_targets.map((t, i) =>
              i === index ? { ...t, content_body: e.target.value } : t,
            ),
          });
        }}
        rows={8}
        required
      />
      <div className="flex justify-end">
        <Button
          aria-label="Delete email"
          disabled={config.email_targets.length === 1}
          onClick={() => {
            onConfigChange?.({
              ...config,
              email_targets: config.email_targets.filter((_, i) => i !== index),
            });
          }}
          variant="outlined"
          color="error"
          size="small"
          className="ml-0 w-36"
        >
          Delete Email
        </Button>
      </div>
    </div>
  );
};
